import { useState, useEffect, useCallback, useRef } from "react";

import { supabase } from "../lib/supabase";

/**
 * Simple hash function for comparing objects
 * Used to determine if profile metadata has actually changed
 * @param {Object} obj - Object to hash
 * @returns {string} - Hash string
 */
const simpleHash = (obj) => {
  if (!obj) return "";
  const str = JSON.stringify(obj, Object.keys(obj).sort());
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash.toString(16);
};

const PROFILE_TIMEOUT_MS = 8000;
const SESSION_TIMEOUT_MS = 10000;
const E2E_MOCK_AUTH_ENABLED = import.meta.env.VITE_E2E_MOCK_AUTH === "true";
const E2E_MOCK_USER = {
  uid: "e2e-user-1",
  id: "e2e-user-1",
  email: "e2e@gryll.ai",
  token: "mock-jwt-token",
  displayName: "E2E User",
  avatar_url: null,
  avatarUrl: null,
  tier: "free",
  credits: 1000,
  isTrial: false,
};

const isInvalidRefreshTokenError = (error) => {
  const message = error?.message?.toLowerCase?.() || "";
  return (
    message.includes("invalid refresh token") ||
    message.includes("refresh token not found")
  );
};

const withTimeout = (
  promise,
  timeoutMs = 5000,
  errorMessage = "Operation timed out",
) => {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error(errorMessage)), timeoutMs);
    }),
  ]);
};

const resolveAuthDisplayName = (authUser) => {
  if (!authUser) return null;
  return (
    authUser.user_metadata?.full_name ||
    authUser.user_metadata?.name ||
    authUser.email?.split("@")[0] ||
    null
  );
};

const resolveAuthAvatar = (authUser) => {
  if (!authUser) return null;
  return (
    authUser.user_metadata?.avatar_url ||
    authUser.user_metadata?.picture ||
    authUser.user_metadata?.photo_url ||
    null
  );
};

const toUserObject = (authUser, profile = null, token = null) => {
  const tier = profile?.tier || profile?.subscription_tier || "free";
  const credits = profile?.credits ?? profile?.credits_balance ?? 0;
  const isTrial = profile?.isTrial || profile?.is_trial_active || false;
  const displayName = profile?.display_name || resolveAuthDisplayName(authUser);
  const avatarUrl =
    profile?.avatar_url || profile?.avatarUrl || resolveAuthAvatar(authUser);

  return {
    uid: authUser.id,
    id: authUser.id,
    email: authUser.email,
    token,
    ...(profile || {}),
    displayName,
    avatar_url: avatarUrl,
    avatarUrl,
    tier,
    credits,
    isTrial,
  };
};

export const useUser = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const mountedRef = useRef(true);
  const activeRequestRef = useRef(0);
  const userRef = useRef(user);
  const loadingRef = useRef(loading);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  const clearInvalidSession = useCallback(async () => {
    try {
      await supabase.auth.signOut({ scope: "local" });
    } catch {
      // Best effort local cleanup.
    }

    if (mountedRef.current) {
      activeRequestRef.current += 1;
      setUser(null);
      setError(null);
      setLoading(false);
    }
  }, []);

  const fetchProfile = useCallback(async (uid) => {
    if (!uid) return null;

    const { data, error: profileError } = await withTimeout(
      supabase.from("profiles").select("*").eq("id", uid).maybeSingle(),
      PROFILE_TIMEOUT_MS,
      "Profile fetch timed out",
    );

    if (profileError) {
      throw profileError;
    }

    return data;
  }, []);

  // Cache for last synced metadata hash to avoid unnecessary updates
  const lastSyncedMetadataRef = useRef(null);

  const buildUserObject = useCallback(
    async (authUser, fallbackProfile = null, token = null) => {
      if (!authUser) return null;

      try {
        const profile = await fetchProfile(authUser.id);

        if (profile) {
          const updates = {};
          const metadataDisplayName = resolveAuthDisplayName(authUser);
          const metadataAvatar = resolveAuthAvatar(authUser);

          // Only update if profile fields are empty and metadata has values
          if (!profile.display_name && metadataDisplayName) {
            updates.display_name = metadataDisplayName;
          }

          if (!profile.avatar_url && metadataAvatar) {
            updates.avatar_url = metadataAvatar;
          }

          // Only perform update if there are actual changes
          if (Object.keys(updates).length > 0) {
            // Create hash of current metadata to check if we've already synced this
            const currentMetadataHash = simpleHash({
              displayName: metadataDisplayName,
              avatarUrl: metadataAvatar,
              profileDisplayName: profile.display_name,
              profileAvatarUrl: profile.avatar_url,
            });

            // Skip if we've already synced this exact metadata
            if (currentMetadataHash === lastSyncedMetadataRef.current) {
              if (import.meta.env.DEV) {
                console.info(
                  "[useUser] Skipping profile sync - metadata unchanged",
                );
              }
            } else {
              try {
                const { data: syncedProfile, error: syncError } = await supabase
                  .from("profiles")
                  .update(updates)
                  .eq("id", authUser.id)
                  .select("*")
                  .maybeSingle();

                if (!syncError && syncedProfile) {
                  // Update cache on successful sync
                  lastSyncedMetadataRef.current = currentMetadataHash;
                  return toUserObject(authUser, syncedProfile, token);
                }
              } catch (syncErr) {
                if (import.meta.env.DEV) {
                  console.warn("[useUser] profile metadata sync skipped", {
                    message: syncErr?.message,
                  });
                }
              }
            }
          }
        }

        return toUserObject(authUser, profile || fallbackProfile, token);
      } catch (profileError) {
        if (import.meta.env.DEV) {
          console.warn("[useUser] profile fetch failed, using fallback", {
            message: profileError?.message,
          });
        }
        return toUserObject(authUser, fallbackProfile, token);
      }
    },
    [fetchProfile],
  );

  const syncFromSession = useCallback(
    async (session) => {
      const requestId = ++activeRequestRef.current;

      if (!session?.user) {
        if (!mountedRef.current || requestId !== activeRequestRef.current)
          return;
        setUser(null);
        setError(null);
        setLoading(false);
        return;
      }

      const fallbackProfile =
        userRef.current?.uid === session.user.id ? userRef.current : null;
      const nextUser = await buildUserObject(
        session.user,
        fallbackProfile,
        session.access_token,
      );

      if (!mountedRef.current || requestId !== activeRequestRef.current) return;

      setUser(nextUser);
      setError(null);
      setLoading(false);
    },
    [buildUserObject],
  );

  const refreshUser = useCallback(async () => {
    if (E2E_MOCK_AUTH_ENABLED) return;

    const currentUser = userRef.current;
    if (!currentUser?.uid) return;

    try {
      const profile = await withTimeout(
        fetchProfile(currentUser.uid),
        PROFILE_TIMEOUT_MS,
        "Refresh user timed out",
      );

      if (!mountedRef.current || !profile) return;

      setUser((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          ...profile,
          avatarUrl:
            profile.avatar_url || profile.avatarUrl || prev.avatarUrl || null,
          tier:
            profile.tier || profile.subscription_tier || prev.tier || "free",
          credits:
            profile.credits ?? profile.credits_balance ?? prev.credits ?? 0,
          isTrial:
            profile.isTrial || profile.is_trial_active || prev.isTrial || false,
        };
      });
    } catch (refreshError) {
      if (isInvalidRefreshTokenError(refreshError)) {
        await clearInvalidSession();
        return;
      }
      if (mountedRef.current) {
        setError(refreshError?.message || "Failed to refresh user profile");
      }
    }
  }, [clearInvalidSession, fetchProfile]);

  useEffect(() => {
    if (E2E_MOCK_AUTH_ENABLED) {
      setUser(E2E_MOCK_USER);
      setError(null);
      setLoading(false);
      return () => {
        mountedRef.current = false;
      };
    }

    mountedRef.current = true;
    // Optimized: Set loading false faster - don't block UI
    // We'll show skeleton states in components instead
    const quickTimeout = setTimeout(() => {
      if (mountedRef.current && loadingRef.current) {
        setLoading(false);
      }
    }, 500); // Max 500ms blocking time

    let subscription = null;

    const init = async () => {
      try {
        const {
          data: { session },
          error: sessionError,
        } = await withTimeout(
          supabase.auth.getSession(),
          SESSION_TIMEOUT_MS,
          "Session restore timed out",
        );

        if (sessionError) throw sessionError;
        await syncFromSession(session);
      } catch (sessionError) {
        if (isInvalidRefreshTokenError(sessionError)) {
          await clearInvalidSession();
          return;
        }
        if (!mountedRef.current) return;
        setError(sessionError?.message || "Failed to restore session");
        setLoading(false);
      } finally {
        clearTimeout(quickTimeout);
      }
    };

    init();

    const { data } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mountedRef.current) return;

      if (event === "SIGNED_OUT") {
        activeRequestRef.current += 1;
        setUser(null);
        setError(null);
        setLoading(false);
        return;
      }

      await syncFromSession(session);
    });

    subscription = data?.subscription;

    return () => {
      mountedRef.current = false;
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [clearInvalidSession, syncFromSession]);

  return { user, loading, error, refreshUser, setUser };
};

export default useUser;
